import React from "react";

import { dlpInsuranceTypes } from "../../../Enums/enumValues";
import { Enum, UseOfName, YesOrNo } from "../../../interfaces/enum";
import { PaymentTerm } from "../../../interfaces/paymentTerm";
import { SectionWrapper } from "../../../layouts/sectionWrapper/SectionWrapper";
import { formatCurrency } from "../../../lib/currencyUtil";
import dateUtil from "../../../lib/dateUtil";
import { getFullName } from "../../../lib/nameUtil";
import { Coverage, Premium, PremiumTerms } from "../../../providers/dlpFlow/DlpFlow.interfaces";
import { isNotUndefined } from "../../../typeguards/isNotUndefined";
import { currencyThousandFormat } from "../../InputFields/sanitizer/currencyFormat";
import { ListWithHeader } from "../../lists/listWithHeader/ListWithHeader";
import { PersonalDetails } from "../../personalDetails/PersonalDetails";
import styles from "./DlpQuotationOverview.module.scss";

interface InsuredData {
  personId: string;
  isDefault: boolean;
  dateOfBirth: string;
  street: string;
  houseNumber: number | null;
  houseNumberAddition?: string;
  city: string;
  postcode: string;
  location?: string;
  emailAddress: string;
  mobileNumber: string;
  salutation: string;
  initials: string;
  firstName: string;
  lastName: string;
  inserts: string;
  isSmoking: YesOrNo;
  hasPartner: boolean;
  partnerInserts: string;
  partnerLastName: string;
  useOfName: UseOfName;
  telephoneNumber: string;
}

interface OldPremiumTerms {
  terms: Premium[];
}

interface Props {
  insurers: Partial<InsuredData>[];
  coverages: Partial<Coverage>[];
  premium: PremiumTerms | OldPremiumTerms | null;
  paymentTerm: PaymentTerm;
  crosswiseInsured: boolean;
  mortgageSupplier?: string;
  insuranceTaker: string[];
  dictionary: Record<string, string>;
  showDisclaimer?: true;
}

export const DlpQuotationOverview = ({
  coverages,
  insurers,
  premium,
  paymentTerm,
  crosswiseInsured,
  mortgageSupplier,
  insuranceTaker,
  dictionary,
  showDisclaimer,
}: Props) => {
  const getTotalPremium = () => {
    if (!premium) {
      return undefined;
    }

    if ("totalPremiumPerTerms" in premium) {
      return premium.totalPremiumPerTerms.find((t) => t.paymentTerm === paymentTerm);
    }

    if ("terms" in premium) {
      return premium.terms.find((t) => t.paymentTerm === paymentTerm);
    }

    return undefined;
  };

  const totalPremiumTerms = getTotalPremium();
  const convertedPaymentTerm = dictionary[`paymentTerm.${paymentTerm.toLowerCase()}`] ?? paymentTerm;

  const totalPremium = totalPremiumTerms?.premium ?? 0;

  const getInsurerAddress = (insurer: Partial<InsuredData>, firstInsurer: Partial<InsuredData>) => ({
    postcode: insurer.postcode ?? firstInsurer.postcode,
    houseNumber: insurer.houseNumber ?? firstInsurer.houseNumber,
    houseNumberAddition: insurer.houseNumberAddition ?? firstInsurer.houseNumberAddition,
    street: insurer.street ?? firstInsurer.street,
    city: insurer.city ?? firstInsurer.city,
    location: insurer.location ?? firstInsurer.location,
  });

  return (
    <SectionWrapper
      headerTitle="Overlijdensrisicoverzekering"
      infoBlocks={[
        {
          label: "Product",
          content: "DELA LeefdoorPlan",
        },
        {
          label: "Totale premie",
          content: `${formatCurrency(totalPremium)} ${convertedPaymentTerm.toLowerCase()}`,
        },
        {
          label: insuranceTaker.length > 1 ? "Verzekeringnemers" : "Verzekeringnemer",
          content: insuranceTaker
            .map((insurerId) => {
              const insured = insurers.find((i) => i.personId === insurerId);

              if (!insured) {
                return undefined;
              }

              return getFullName(insured);
            })
            .join("\n"),
        },
      ]}
    >
      <ListWithHeader
        columns={[
          { accessor: "key", Header: "Verzekering", flex: 1.35 },
          { accessor: "value", flex: 3 },
        ]}
        data={[
          {
            key: "Betaaltermijn",
            value: convertedPaymentTerm,
          },
          crosswiseInsured
            ? {
                key: "Kruislingse premieplichtigheid",
                value: "Je hebt gekozen om kruislingse premieplichtigheid toe te passen op deze polis.",
              }
            : undefined,
          mortgageSupplier
            ? {
                key: "Verpanding",
                value: mortgageSupplier,
              }
            : undefined,
        ].filter(isNotUndefined)}
        hideLastBorder
      />

      {coverages.map((coverage, index) => {
        const insureds = coverage.effectiveInsured
          ?.map((ei) => insurers.find((i) => i.personId === ei))
          .filter(isNotUndefined);

        if (insureds === undefined) {
          return null;
        }

        const insuranceType =
          (dlpInsuranceTypes() as Enum[]).find((x) => x.value === coverage.insuranceType)?.label ?? "-";

        const coveragePremium =
          premium && "premiumPerCoverage" in premium
            ? premium.premiumPerCoverage?.find((ppc) => ppc.coverageId === coverage.coverageId)
            : undefined;
        const coveragePremiumPerPaymentTerm = coveragePremium?.premiumsPerTerms.find(
          (t) => t.paymentTerm === paymentTerm
        );

        const data = [
          {
            key: insureds.length > 1 ? "Verzekerden" : "Verzekerde",
            value: insureds
              ?.map(
                (i) =>
                  `${getFullName(i)}${
                    i.dateOfBirth ? ` - ${dateUtil.dutchDateToDateWithWrittenMonth(i.dateOfBirth)}` : ""
                  } - roken: ${i.isSmoking === "Yes" ? "ja" : "nee"}`
              )
              .join("\n"),
          },
          {
            key: "Ingangsdatum",
            value: coverage.effectiveDate ? dateUtil.dutchDateToDateWithWrittenMonth(coverage.effectiveDate) : "-",
          },
          {
            key: "Verzekerd bedrag",
            value: coverage.insuredAmount ? currencyThousandFormat(coverage.insuredAmount) : "-",
          },
          {
            key: "Looptijd verzekering",
            value: `${coverage.duration} jaar`,
          },
          {
            key: "Verzekeringsvorm",
            value: insuranceType,
          },
          coverage.insuranceType === "Annuity"
            ? {
                key: "Annuïteitenpercentage",
                value: `${coverage.reductionRate} %`,
              }
            : undefined,
          coveragePremiumPerPaymentTerm !== undefined
            ? {
                key: "Premie",
                value: formatCurrency(coveragePremiumPerPaymentTerm.premium),
              }
            : undefined,
        ].filter(isNotUndefined);

        return (
          <ListWithHeader
            key={`policyCoverageData${index}`}
            columns={[
              { accessor: "key", Header: `Dekking ${index + 1}`, flex: 1.35 },
              { accessor: "value", flex: 3 },
            ]}
            data={data}
          />
        );
      })}
      <div className={styles.dlpQuotationOverview__coveragePremium}>
        <div className={styles.dlpQuotationOverview__coveragePremiumContainerLeft}>
          <p>Totale premie {convertedPaymentTerm.toLowerCase()} inclusief kosten</p>
        </div>
        <div className={styles.dlpQuotationOverview__coveragePremiumContainerRight}>
          <p>{formatCurrency(totalPremium)}</p>
        </div>
      </div>

      {showDisclaimer && (
        <p className={styles.dlpQuotationOverview__disclaimer_text}>
          Aan deze offerte kun je geen rechten ontlenen. We berekenen je definitieve premie als we de ingevulde
          gezondheidsvragen hebben ontvangen en de definitieve ingangsdatum bekend is.
        </p>
      )}

      <h4 className={styles.dlpQuotationOverview__sectionHeader}>Contactgegevens</h4>
      <div className={styles.dlpQuotationOverview__contactDetails}>
        {insurers.map((insurer) => {
          const insurerAddress = getInsurerAddress(insurer, insurers[0]);

          return <PersonalDetails key={insurer.personId} details={{ ...insurer, ...insurerAddress }} />;
        })}
      </div>
    </SectionWrapper>
  );
};
